import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MarketTrendModal from '../components/MarketTrendModal';
import { api } from '../api/client';
import EconomicCalendarWidget from '../components/EconomicCalendarWidget';

interface HomeData {
    date: string;
    marketSnapshot: {
        date: string;
        fearGreedValue: number | null;
        fearGreedLabel: string | null;
        kospiForeignNet: number | null;
        kospiInstitutionNet: number | null;
        kosdaqForeignNet: number | null;
        kosdaqInstitutionNet: number | null;
        commentary: string | null;
    } | null;
    activePlan: {
        id: string;
        planDate: string;
        version: number;
        items: Array<{
            index: string;
            state: string;
            action: string;
            memo: string;
        }>;
        globalMemo: string | null;
    } | null;
    tomorrowPlan: {
        id: string;
        planDate: string;
        version: number;
        items: Array<{
            index: string;
            state: string;
            action: string;
            memo: string;
        }>;
        globalMemo: string | null;
    } | null;
    positionsNeedingReview: Array<{
        id: string;
        symbol: string;
        market: string;
        pnlPct: number | null;
    }>;
    activePositions: Array<{
        id: string;
        symbol: string;
        market: string;
        status: string;
        pnlPct: number | null;
        hasReview: boolean;
    }>;
    recentPositions: Array<{
        id: string;
        symbol: string;
        market: string;
        status: string;
        pnlPct: number | null;
        hasReview: boolean;
    }>;

    monthlyEvents?: Array<{
        id: string;
        date: string;
        time: string;
        currency: string;
        event: string;
        importance: number;
        actual: string;
        forecast: string;
        previous: string;
    }>;
}

export default function Home() {
    const [data, setData] = useState<HomeData | null>(null);
    const [loading, setLoading] = useState(true);
    const [trendModalOpen, setTrendModalOpen] = useState(false);
    const [trendType, setTrendType] = useState<'FEAR_GREED' | 'KOSPI' | 'KOSDAQ'>('FEAR_GREED');
    const [historyData, setHistoryData] = useState<any[]>([]);

    useEffect(() => {
        initializeHome();
    }, []);

    async function initializeHome() {
        // Check if data needs refreshing
        const lastUpdate = localStorage.getItem('last_market_update_ts');
        const now = Date.now();
        const needsUpdate = !lastUpdate || now - Number(lastUpdate) > 3600000; // 1 hour

        if (needsUpdate) {
            console.log('Data stale, updating before display...');
            // Fetch fresh data first, THEN load home data
            try {
                await Promise.all([
                    api.updateMarketData().catch(e => console.error('Market update failed', e)),
                    api.request('/market-data/calendar/fetch', { method: 'POST' }).catch(e => console.error('Calendar update failed', e))
                ]);
                localStorage.setItem('last_market_update_ts', now.toString());
            } catch (e) {
                console.error('Auto-update failed:', e);
            }
        }

        // Now load home data (will have fresh data if update succeeded)
        const response = await api.getHome();
        if (response.success) {
            setData(response.data);
        }
        setLoading(false);
    }

    async function loadHomeData() {
        const response = await api.getHome();
        if (response.success) {
            setData(response.data);
        }
    }

    const handleUpdateMarketData = async () => {
        if (!confirm('시장 데이터를 최신 상태로 업데이트하시겠습니까?')) return;

        setLoading(true);
        try {
            const res = await api.updateMarketData();
            if (res.success) {
                alert('시장 데이터가 업데이트되었습니다.');
                await loadHomeData(); // Reload data
            } else {
                alert('업데이트 실패: ' + res.error);
            }
        } catch (e) {
            console.error(e);
            alert('업데이트 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenTrend = async (type: 'FEAR_GREED' | 'KOSPI' | 'KOSDAQ') => {
        setTrendType(type);
        setTrendModalOpen(true);
        try {
            const response = await api.getMarketHistory();
            if (response.success) {
                setHistoryData(response.data);
            }
        } catch (e) {
            console.error('Failed to fetch history', e);
        }
    };

    const TrendIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5 cursor-pointer hover:text-primary transition-colors">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
    );

    if (loading) {
        return (
            <div className="loading-screen">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    const formatPnl = (pnl: number | null | undefined) => {
        if (pnl == null) return '-';
        const formatted = pnl.toFixed(2);
        return pnl >= 0 ? `+${formatted}%` : `${formatted}%`;
    };

    const getPnlClass = (pnl: number | null | undefined) => {
        if (pnl == null) return '';
        return pnl >= 0 ? 'positive' : 'negative';
    };

    // Calculate tomorrow for link using Korean timezone (UTC+9)
    const kstNow = new Date(Date.now() + (9 * 60 * 60 * 1000));
    const tomorrow = new Date(kstNow);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];

    return (
        <div>
            {/* Modal */}
            <MarketTrendModal
                isOpen={trendModalOpen}
                onClose={() => setTrendModalOpen(false)}
                data={historyData}
                type={trendType}
            />

            <div className="page-header flex justify-between items-center">
                <div>
                    <h1 className="page-title inline-block mr-sm">홈</h1>
                    <span className="text-muted">{data?.date}</span>
                </div>
                <button onClick={handleUpdateMarketData} className="btn btn-ghost btn-sm">
                    🔄 데이터 업데이트
                </button>
            </div>

            {/* Market Overview */}
            <div className="grid grid-cols-3 mb-lg">
                <div className="card relative">
                    <div className="flex justify-between items-start mb-md">
                        <h3 className="card-title">
                            Fear & Greed
                            {data?.marketSnapshot?.date && (
                                <span className="text-xs text-muted font-normal ml-sm">
                                    ({data.marketSnapshot.date})
                                </span>
                            )}
                        </h3>
                        <div onClick={() => handleOpenTrend('FEAR_GREED')} title="추세 보기">
                            <TrendIcon />
                        </div>
                    </div>
                    {data?.marketSnapshot ? (
                        <div className="text-center">
                            <div className="stat-value" style={{ fontSize: '2.5rem' }}>
                                {data.marketSnapshot.fearGreedValue ?? '-'}
                            </div>
                            <div className={`stat-label font-bold ${(data.marketSnapshot.fearGreedLabel?.toLowerCase().includes('fear') ?? false) ? 'text-danger' :
                                (data.marketSnapshot.fearGreedLabel?.toLowerCase().includes('greed') ?? false) ? 'text-success' : 'text-muted'
                                }`}>
                                {data.marketSnapshot.fearGreedLabel ? data.marketSnapshot.fearGreedLabel.toUpperCase() : '데이터 없음'}
                            </div>
                        </div>
                    ) : (
                        <p className="text-muted text-center">데이터 없음</p>
                    )}
                </div>

                <div className="card relative">
                    <div className="flex justify-between items-start mb-md">
                        <h3 className="card-title">
                            KOSPI 수급
                            {data?.marketSnapshot?.date && (
                                <span className="text-xs text-muted font-normal ml-sm">
                                    ({data.marketSnapshot.date})
                                </span>
                            )}
                        </h3>
                        <div onClick={() => handleOpenTrend('KOSPI')} title="추세 보기">
                            <TrendIcon />
                        </div>
                    </div>
                    {data?.marketSnapshot ? (
                        <div className="flex flex-col gap-sm text-sm">
                            <div className="flex justify-between">
                                <span className="text-muted">외국인</span>
                                <span className={getPnlClass(data.marketSnapshot.kospiForeignNet)}>
                                    {data.marketSnapshot.kospiForeignNet?.toLocaleString() ?? '-'}억
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted">기관</span>
                                <span className={getPnlClass(data.marketSnapshot.kospiInstitutionNet)}>
                                    {data.marketSnapshot.kospiInstitutionNet?.toLocaleString() ?? '-'}억
                                </span>
                            </div>
                        </div>
                    ) : (
                        <p className="text-muted text-center">데이터 없음</p>
                    )}
                </div>

                <div className="card">
                    <h3 className="card-title mb-md">
                        KOSDAQ 수급
                        {data?.marketSnapshot?.date && (
                            <span className="text-xs text-muted font-normal ml-sm">
                                ({data.marketSnapshot.date})
                            </span>
                        )}
                    </h3>
                    {data?.marketSnapshot ? (
                        <div className="flex flex-col gap-sm text-sm">
                            <div className="flex justify-between">
                                <span className="text-muted">외국인</span>
                                <span className={getPnlClass(data.marketSnapshot.kosdaqForeignNet)}>
                                    {data.marketSnapshot.kosdaqForeignNet?.toLocaleString() ?? '-'}억
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-muted">기관</span>
                                <span className={getPnlClass(data.marketSnapshot.kosdaqInstitutionNet)}>
                                    {data.marketSnapshot.kosdaqInstitutionNet?.toLocaleString() ?? '-'}억
                                </span>
                            </div>
                        </div>
                    ) : (
                        <p className="text-muted text-center">데이터 없음</p>
                    )}
                </div>
            </div>

            {/* Commentary */}
            {data?.marketSnapshot?.commentary && (
                <div className="card mb-lg">
                    <h3 className="card-title mb-md">시황 요약</h3>
                    <p>{data.marketSnapshot.commentary}</p>
                </div>
            )}



            {/* Economic Calendar */}
            <EconomicCalendarWidget
                events={(data as any)?.monthlyEvents || []}
                onRefresh={loadHomeData}
            />

            {/* Today's Scenario */}
            <div className="card mb-lg">
                <div className="card-header flex justify-between items-center">
                    <div className="flex items-center gap-sm">
                        <h3 className="card-title mb-0">오늘의 시나리오</h3>
                        {data?.activePlan && (
                            <span className="badge badge-info">v{data.activePlan.version}</span>
                        )}
                    </div>
                    {data?.activePlan && (
                        <Link to={`/journal/${data.date}`} className="btn btn-secondary btn-sm">
                            수정하기
                        </Link>
                    )}
                </div>

                {data?.activePlan ? (
                    <>
                        {data.activePlan.items.map((item, idx) => (
                            <div key={idx} className="scenario-card">
                                <div className="scenario-header">
                                    <span className="scenario-index">{item.index}</span>
                                    <span className={`scenario-state ${item.state.toLowerCase()}`}>
                                        {item.state === 'UP' ? '📈' : item.state === 'DOWN' ? '📉' : '➡️'} {item.state === 'UP' ? '상승' : item.state === 'DOWN' ? '하락' : '보합'}
                                    </span>
                                </div>
                                <p className="font-semibold mb-sm">{item.action}</p>
                                {item.memo && <p className="text-sm text-muted">{item.memo}</p>}
                            </div>
                        ))}
                        {data.activePlan.globalMemo && (
                            <p className="text-sm text-muted mt-md">
                                <strong>메모:</strong> {data.activePlan.globalMemo}
                            </p>
                        )}
                    </>
                ) : (
                    <div className="empty-state">
                        <p>오늘의 시나리오가 없습니다</p>
                        <Link to={`/journal/${data?.date || kstNow.toISOString().split('T')[0]}`} className="btn btn-primary mt-md">
                            시나리오 작성하기
                        </Link>
                    </div>
                )}
            </div>

            {/* Positions Needing Review */}
            {data?.positionsNeedingReview && data.positionsNeedingReview.length > 0 && (
                <div className="card mb-lg">
                    <h3 className="card-title mb-md">복기 필요</h3>
                    <div className="flex flex-col gap-sm">
                        {data.positionsNeedingReview.map((pos) => (
                            <Link key={pos.id} to={`/positions/${pos.id}`} className="position-card">
                                <div className="position-info">
                                    <div>
                                        <div className="position-symbol">{pos.symbol}</div>
                                        <div className="position-market">{pos.market}</div>
                                    </div>
                                </div>
                                <div className={`position-pnl ${getPnlClass(pos.pnlPct)}`}>
                                    {formatPnl(pos.pnlPct)}
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Today's Portfolio (Active) */}
            <div className="card mb-lg">
                <div className="card-header">
                    <h3 className="card-title">
                        <Link to="/new" className="text-decoration-none hover-underline">오늘의 포트폴리오</Link>
                    </h3>
                    <div className="flex gap-sm">
                        <Link to="/new" className="btn btn-primary btn-sm">등록하기</Link>
                        <Link to="/positions" className="btn btn-secondary btn-sm">모두 보기</Link>
                    </div>
                </div>

                {data?.activePositions && data.activePositions.length > 0 ? (
                    <div className="flex flex-col gap-sm">
                        {data.activePositions.slice(0, 5).map((pos) => (
                            <Link key={pos.id} to={`/positions/${pos.id}`} className="position-card">
                                <div className="position-info">
                                    <div>
                                        <div className="position-symbol">{pos.symbol}</div>
                                        <div className="position-market">{pos.market}</div>
                                    </div>
                                    <span className={`badge ${pos.status === 'OPEN' ? 'badge-success' : 'badge-neutral'}`}>
                                        {pos.status === 'OPEN' ? '보유중' : '청산'}
                                    </span>
                                </div>
                                <div className={`position-pnl ${getPnlClass(pos.pnlPct)}`}>
                                    {formatPnl(pos.pnlPct)}
                                </div>
                            </Link>
                        ))}
                    </div>
                ) : (
                    <div className="empty-state">
                        <p>포트폴리오가 없습니다</p>
                        <Link to="/new" className="btn btn-primary mt-md">첫 포트폴리오 기록하기</Link>
                    </div>
                )}
            </div>

            {/* Tomorrow's Scenario */}
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">내일의 시나리오 ({tomorrowStr})</h3>
                    {data?.tomorrowPlan && (
                        <span className="badge badge-info">v{data.tomorrowPlan.version}</span>
                    )}
                </div>

                {data?.tomorrowPlan ? (
                    <>
                        {data.tomorrowPlan.items.map((item, idx) => (
                            <div key={idx} className="scenario-card">
                                <div className="scenario-header">
                                    <span className="scenario-index">{item.index}</span>
                                    <span className={`scenario-state ${item.state.toLowerCase()}`}>
                                        {item.state === 'UP' ? '📈 상승' : item.state === 'DOWN' ? '📉 하락' : '➡️ 보합'}
                                    </span>
                                </div>
                                <p className="font-semibold mb-md">{item.action}</p>
                                {item.memo && <p className="text-sm text-muted">{item.memo}</p>}
                            </div>
                        ))}
                        {data.tomorrowPlan.globalMemo && (
                            <div className="mt-md text-sm text-muted">
                                <strong>메모:</strong> {data.tomorrowPlan.globalMemo}
                            </div>
                        )}
                        <div className="mt-md text-right">
                            <Link to={`/journal/${tomorrowStr}`} className="btn btn-sm btn-ghost">
                                수정하기
                            </Link>
                        </div>
                    </>
                ) : (
                    <div className="empty-state">
                        <p>내일 시나리오가 없습니다</p>
                        <Link to={`/journal/${tomorrowStr}`} className="btn btn-primary mt-md">
                            내일 시나리오 작성하기
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
